import React, { useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import LooksOneIcon from '@mui/icons-material/LooksOne'
import LooksTwoIcon from '@mui/icons-material/LooksTwo'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { OpenInNew } from '@mui/icons-material'
import moment from 'moment'
import { getStageMeta } from '../stageMeta'
import useIsMobile from '../../../helpers/useIsMobile'

const VIEWS = [
  { value: 'active', label: 'Active' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

// Rejected applications keep a userInfo snapshot because the applicant's
// login is deleted on rejection; active/approved rows still have userAccount.
const applicantName = (row) => ({
  firstName: row?.userAccount?.firstName ?? row?.userInfo?.firstName ?? '',
  lastName: row?.userAccount?.lastName ?? row?.userInfo?.lastName ?? '',
})

const applicantEmail = (row) =>
  row?.userAccount?.email ?? row?.userInfo?.email ?? ''

// Applications rejected before the userInfo snapshot existed have no name left
// to show - the login was deleted, so say so rather than rendering a blank cell.
const MissingApplicantName = ({ row, field }) => {
  const name = applicantName(row)

  if (name.firstName || name.lastName) return name[field]
  if (field !== 'lastName') return ''

  return (
    <Typography variant="body2" color="text.disabled" fontStyle="italic">
      Applicant record removed
    </Typography>
  )
}

const ApplicationsDashboard = () => {
  const [applications, setApplications] = useState([])
  const [view, setView] = useState('active')
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  const navigate = useNavigate()
  const isMobile = useIsMobile()
  // Phones: one combined "Applicant" column + stage + open; the detail columns
  // stay available through the grid's Columns toolbar button.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    email: false,
  })
  useEffect(() => {
    setColumnVisibilityModel(
      isMobile
        ? {
            surname: false,
            firstname: false,
            clinicalRole: false,
            referenceResponses: false,
            nextInterview: false,
            updatedAt: false,
          }
        : { email: false },
    )
  }, [isMobile])

  const getApplications = (selectedView) => {
    setLoading(true)
    API.get(`/employees/open-applications?view=${selectedView}`)
      .then((res) => {
        if (res.status === 200) {
          setApplications(res.data)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => getApplications(view), [view])

  return (
    <Stack gap={2}>
      <Typography variant="title2">Employee Applications</Typography>
      <Tabs
        value={view}
        onChange={(e, newView) => setView(newView)}
        sx={{ minHeight: 36 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {VIEWS.map(({ value, label }) => (
          <Tab key={value} value={value} label={label} sx={{ minHeight: 36 }} />
        ))}
      </Tabs>
      {fetchError && (
        <Alert severity="error">
          Unable to load applications. Please try again.
        </Alert>
      )}
      <CustomDataGrid
        rows={applications}
        loading={loading}
        maxHeight={500}
        norowsoverlay={
          view === 'active' ? (
            <Alert severity="info">
              No applications yet. Candidates apply through your public
              application form — enable it and choose which roles are open in
              Site Configuration → Employees → Applications. Submissions then
              arrive here for review.
            </Alert>
          ) : (
            <Alert severity="info">No {view} applications to show yet.</Alert>
          )
        }
        sx={{
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        slotProps={{
          toolbar: {
            csvOptions: { allColumns: true },
          },
        }}
        disableColumnSelector
        columns={[
          {
            field: 'applicant',
            headerName: 'Applicant',
            flex: 1,
            minWidth: 150,
            valueGetter: (value, row) => {
              const name = applicantName(row)
              return [name.lastName, name.firstName].filter(Boolean).join(', ')
            },
            renderCell: ({ row }) => {
              const name = applicantName(row)
              if (!name.firstName && !name.lastName) {
                return <MissingApplicantName row={row} field="lastName" />
              }
              return (
                <Stack minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {name.lastName}, {name.firstName}
                  </Typography>
                  {row?.employee?.clinicalRole && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.employee.clinicalRole}
                    </Typography>
                  )}
                </Stack>
              )
            },
          },
          // {
          //   field: 'surname',
          //   headerName: 'Surname',
          //   renderCell: ({ row }) => (
          //     <MissingApplicantName row={row} field="lastName" />
          //   ),
          //   valueGetter: (value, row) => applicantName(row).lastName,
          //   flex: 0.5,
          // },
          // {
          //   field: 'firstname',
          //   headerName: 'First Name',
          //   renderCell: ({ row }) => (
          //     <MissingApplicantName row={row} field="firstName" />
          //   ),
          //   valueGetter: (value, row) => applicantName(row).firstName,
          //   flex: 0.5,
          // },
          {
            field: 'clinicalRole',
            headerName: 'Clinical Role',
            renderCell: ({ row }) => row?.employee?.clinicalRole ?? '',
            valueGetter: (value, row) => row?.employee?.clinicalRole ?? '',
            flex: 0.5,
          },
          {
            field: 'email',
            headerName: 'Email',
            valueGetter: ((value, row) => applicantEmail(row)),
          },
          {
            field: 'stage',
            headerName: 'Stage',
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            minWidth: isMobile ? 110 : undefined,
            valueGetter: (value, row) => row.stage ?? row.state,
            renderCell: ({ row }) => {
              const stage = row.stage ?? row.state
              const meta = getStageMeta(stage)
              return (
                <Tooltip title={meta.description}>
                  <Chip
                    size="small"
                    sx={{ borderRadius: 1 }}
                    color={meta.color}
                    label={stage}
                  />
                </Tooltip>
              )
            },
          },
          ...(view === 'active'
            ? [
                {
                  field: 'referenceResponses',
                  headerName: 'References',
                  align: 'center',
                  headerAlign: 'center',
                  sortable: false,
                  disableExport: true,
                  renderCell: ({ row }) => (
                    <Box
                      width="100%"
                      height="100%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Stack direction="row" alignItems="center">
                        <LooksOneIcon
                          color={
                            Object.keys(row?.reference1?.response ?? [])
                              .length > 0
                              ? 'success'
                              : 'disabled'
                          }
                        />
                        <LooksTwoIcon
                          color={
                            Object.keys(row?.reference2?.response ?? [])
                              .length > 0
                              ? 'success'
                              : 'disabled'
                          }
                        />
                      </Stack>
                    </Box>
                  ),
                },
                {
                  field: 'nextInterview',
                  headerName: 'Next Interview',
                  align: 'center',
                  headerAlign: 'center',
                  flex: 0.7,
                  valueGetter: (value, row) => {
                    const upcoming = (row.interviews ?? [])
                      .filter(
                        (interview) =>
                          (interview.outcome ?? 'Pending') === 'Pending' &&
                          interview.scheduledStart,
                      )
                      .sort(
                        (a, b) =>
                          new Date(a.scheduledStart) -
                          new Date(b.scheduledStart),
                      )
                    return upcoming[0]?.scheduledStart ?? null
                  },
                  renderCell: ({ value }) =>
                    value ? moment(value).format('Do MMM HH:mm') : '—',
                },
              ]
            : [
                {
                  field: 'updatedAt',
                  headerName: view === 'approved' ? 'Approved' : 'Rejected',
                  align: 'center',
                  headerAlign: 'center',
                  flex: 0.7,
                  valueGetter: (value, row) => row.updatedAt,
                  renderCell: ({ value }) =>
                    value ? moment(value).format('Do MMM YYYY') : '',
                },
              ]),
          {
            field: 'actions',
            headerName: '',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            width: isMobile ? 56 : 120,
            disableExport: true,
            renderCell: ({ row }) => (
              <IconButton
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(`/employees/applications/${row._id}`, '_blank')
                }}
              >
                <Tooltip title="Open Application in New Tab">
                  <OpenInNew fontSize="10" />
                </Tooltip>
              </IconButton>
            ),
          },
        ]}
        getOptionKey={({ row }) => row._id}
        onRowClick={({ row }) => navigate('/employees/applications/' + row._id)}
      />
    </Stack>
  )
}

export default ApplicationsDashboard
